// Shared wording for a quote's expiry, so the customer's Quotes table, the
// vendor's Leads table and the quote document all read the same way.
// Timestamps arrive from the API as epoch-millisecond strings.
import { TFunction } from "i18next";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export interface ExpiryInfo {
  /** Short label for a table cell, e.g. "in 9 days" or "Expired 3 days ago". */
  label: string;
  /** Formatted expiry date, e.g. "8/17/2026". Empty when there is no expiry. */
  date: string;
  isExpired: boolean;
}

export const parseTimestamp = (value?: string | null): Date | null => {
  if (!value) return null;

  const parsed = new Date(Number(value));

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatExpiry = (
  t: TFunction<"quotes">,
  formatDate: (date: Date) => string,
  expiresAt?: string | null,
  isExpired?: boolean,
): ExpiryInfo => {
  const expiry = parseTimestamp(expiresAt);

  if (!expiry) {
    return { label: "—", date: "", isExpired: false };
  }

  const date = formatDate(expiry);
  // Prefer the server's verdict (one clock) and fall back to comparing here.
  const expired = isExpired ?? expiry.getTime() < Date.now();
  const days = Math.round(Math.abs(expiry.getTime() - Date.now()) / MS_PER_DAY);

  if (expired) {
    return {
      label:
        days === 0
          ? t("expiry.expired_today")
          : t("expiry.expired_ago", { count: days }),
      date,
      isExpired: true,
    };
  }

  return {
    label:
      days === 0
        ? t("expiry.expires_today")
        : t("expiry.expires_in", { count: days }),
    date,
    isExpired: false,
  };
};
